import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom'
import { useSelector, useStore } from 'react-redux'
import dagre from '@dagrejs/dagre';
import {
    useNodesState,
    useEdgesState,
    addEdge,
    Controls,
    Background,
    MiniMap,
    useReactFlow,
    ReactFlow,
    MarkerType,
} from '@xyflow/react';
import _ from 'lodash'
import ConnectionLine from './ConnectionLine';
import N8NEdge from './edge/N8NEdge';
import Toolbar from './toolkit/Toolbar';
import Sidebar from './toolkit/Sidebar';
import DropdownNode from './node/DropdownNode';
import TextfieldNode from './node/TextfieldNode';
import CalendarNode from './node/CalendarNode';
import RadioNode from './node/RadioNode';
import CheckboxNode from './node/CheckboxNode';
import TextareaNode from './node/TextareaNode';
import RatingNode from './node/RatingNode';
import ImagelistNode from './node/ImagelistNode';
import DropdownTextfieldNode from './node/DropdownTextfieldNode';
import FullSizeBtn from './controls/FullSizeBtn';
import { KEYS_OF_DRAFT_DATA, NODE_TYPES, OPTIONS_TO_DISPLAY, PREFIX_OF_TEMP_NODE } from './constant';
import { post, put } from '../../helper/baseFetch';
import { API_PATH } from '../../Constants/path';
import { constants } from '../../Constants/constants';
import '@xyflow/react/dist/style.css';
import './flow.css'
import useNodeCRUD from './hooks/useNodeCRUD';
import { Snackbar } from '@mui/material';


const fullscreenSx = { position: 'absolute', inset: 0, zIndex: 1000, width: '100vw', height: '100vh', background: '#fff' }
const normalscreenSx = { position: 'relative', width: '99%', height: '99%', border: '1px solid #3333' }

// custom node types
const nodeTypes = {
    [NODE_TYPES.DROPDOWN]: DropdownNode,
    [NODE_TYPES.TEXT_FIELD]: TextfieldNode,
    [NODE_TYPES.CALENDAR]: CalendarNode,
    [NODE_TYPES.RADIO]: RadioNode,
    [NODE_TYPES.CHECKBOX]: CheckboxNode,
    [NODE_TYPES.TEXT_AREA]: TextareaNode,
    [NODE_TYPES.RATING]: RatingNode,
    [NODE_TYPES.IMAGELIST]: ImagelistNode,
    [NODE_TYPES.DROPDOWN_TEXT_FIELD]: DropdownTextfieldNode,
};

// custom edge types
const edgeTypes = {
    default: N8NEdge
}

// node settings
const nodeWidth = 250;
const nodeHeight = 100;
const optionHeight = 0

// related to auto layout
const dagreGraph = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));


const SurveyFlow = ({
    initList,
    updateDataToStore,
    handleSave
}) => {

    const store = useStore()
    const savedSurveyFlow = useSelector(state => state.surveyFlow.ui.surveyFlow)

    const location = useLocation()

    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const { screenToFlowPosition, getNodes, getEdges, getViewport, fitView } = useReactFlow();

    const { onNodeClone } = useNodeCRUD()

    const [selectedOption, setSelectedOption] = useState('1');
    const [fullscreen, setFullscreen] = useState(false)
    const [nodeIdToClone, setNodeIdToClone] = useState(null)
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: ''
    })

    // handle connection line create
    const onConnect = useCallback(
        (params) => setEdges((eds) => addEdge({ ...params, data: { condition: selectedOption } }, eds)),
        [selectedOption]
    );

    // handle toolbar node onDrop 
    const onDrop = useCallback((event) => {
        event.preventDefault();

        const type = event.dataTransfer.getData('application/reactflow');
        const position = screenToFlowPosition({ x: event.clientX, y: event.clientY - 40 });

        const newNode = {
            id: `${PREFIX_OF_TEMP_NODE}${Date.now()}`,
            type,
            position,
            data: {
                id: `${PREFIX_OF_TEMP_NODE}${Date.now()}`,
                "sectionId": null,
                "question": {
                    "EN": type,
                    "ZH_CN": type,
                    "ZH_HK": type
                },
                "answerLabel": {
                    "EN": type,
                    "ZH_CN": type,
                    "ZH_HK": type
                },
                "type": type,
                "inputMode": null,
                "inputRegex": null,
                "minSize": type == NODE_TYPES.RADIO ? 1 : 0,
                "maxSize": type == NODE_TYPES.RADIO ? 1 : 0,
                "mandatory": false,
                "answerOptionList": null,
                "sequence": 10,
                "status": "A",
                "parentQuestionId": null,
                "nextQuestionMap": null,
                "filterKey": null
            },
        };

        setNodes((nds) => nds.concat(newNode));
    }, [])
    const onDragOver = useCallback((e) => e.preventDefault(), [])

    // fullscreen button on the Controls panel
    const handleFullscreen = useCallback(() => setFullscreen(!fullscreen), [fullscreen])

    // auto layout
    const getLayoutedElements = (nodes, edges, direction = 'LR') => {
        const isHorizontal = direction === 'LR';
        dagreGraph.setGraph({ rankdir: direction, ranksep: 250, nodesep: 50 });

        nodes.forEach((node) => {
            const height = node?.data?.answerOptionList ? (
                    nodeHeight + optionHeight * (node?.data?.answerOptionList?.length > OPTIONS_TO_DISPLAY ? (OPTIONS_TO_DISPLAY + 1) : node?.data?.answerOptionList?.length)
                ) : nodeHeight

            dagreGraph.setNode(
                node.id, {
                width: node.measured?.width ? node.measured.width : nodeWidth,
                height: node.measured?.height ? node.measured.height : height
            });
        });

        edges.forEach((edge) => {
            dagreGraph.setEdge(edge.source, edge.target);
        });

        dagre.layout(dagreGraph);

        const newNodes = nodes.map((node) => {
            const nodeWithPosition = dagreGraph.node(node.id);
            const newNode = {
                ...node,
                // We are shifting the dagre node position (anchor=center center) to the top left
                // so it matches the React Flow node anchor point (top left).
                position: {
                    x: nodeWithPosition.x - nodeWidth / 2,
                    y: nodeWithPosition.y - (
                        node?.data?.answerOptionList ? nodeHeight + optionHeight * (
                            node?.data?.answerOptionList?.length > OPTIONS_TO_DISPLAY ? (OPTIONS_TO_DISPLAY + 1) : node?.data?.answerOptionList?.length
                        )
                            : nodeHeight
                    ) / 2,
                },
            };

            return newNode;
        });

        return { nodes: newNodes, edges };
    };

    // initialize api's questionList to nodes/edges
    function initData(dataToInit) {
        if (dataToInit) {
            const initNodes = dataToInit?.map((item, idx) => ({
                "id": item?.id,
                "type": item?.type,
                "data": item,
            }))

            const initEdges = dataToInit?.map((item, idx) => {
                return {
                    "id": item?.id,
                    "source": item?.id,
                    "target": item?.nextQuestionMap,
                }
            })?.filter(i => i?.target)

            const { nodes: n, edges: e } = getLayoutedElements(initNodes, initEdges)
            setNodes(n)
            setEdges(e)
            fitView()
        } else {
            setNodes([])
            setEdges([])
            fitView()
        }
    }

    const onCopy = useCallback((e) => {
        if (nodes.filter(i => i.id == e?.target?.dataset?.id).length === 1) {
            setNodeIdToClone(e?.target?.dataset?.id)
            setSnackbar({
                open: true,
                message: 'Copied'
            })
        } else {
            setNodeIdToClone(null)
        }
    }, [nodes])

    const onPaste = useCallback((e) => {
        if (nodeIdToClone) {
            onNodeClone(e, nodeIdToClone)
            setSnackbar({
                open: true,
                message: 'Pasted'
            })
        }
    }, [nodeIdToClone])

    const onLayout = useCallback(() => {
        const currentNodes = getNodes();
        
        // ⚠️ Check if dimensions are available before proceeding
        const allDimensionsSet = currentNodes.every(n => n.width !== null && n.height !== null);
        if (currentNodes.length > 0 &&  allDimensionsSet) {
        const { nodes: n, edges: e } = getLayoutedElements(currentNodes, edges)
        setNodes(n)
        setEdges(e)
        fitView()
        }
    }, [getNodes, edges, setNodes]);

  // 💡 SOLUTION 1: Run with a small delay after initial render
  useEffect(() => {
      // Set a small timeout (e.g., 50ms) to ensure React Flow finishes its first render cycle
      const timer = setTimeout(() => {
          onLayout();
      }, 50); 

      return () => clearTimeout(timer); // Cleanup timer
  }, [onLayout]); // Only runs after onLayout is defined

    useEffect(() => {
        initData(initList)
    }, [initList])

    useEffect(() => {
        localStorage.setItem(KEYS_OF_DRAFT_DATA, JSON.stringify(savedSurveyFlow))
        initData(savedSurveyFlow?.questionList)
    }, [savedSurveyFlow])

    // trigger when click browser's refresh, reinput address, close tab and close browser
    // const handleBeforeUnload = useCallback((e) => {
    //     e.preventDefault();
    //     e.returnValue = ''; // must set returnValue
    //     return 'do you want to save the changed content???';
    // }, []);

    // useEffect(() => {
    //     window.addEventListener('beforeunload', handleBeforeUnload);

    //     return () => {
    //         window.removeEventListener('beforeunload', handleBeforeUnload);
    //     };
    // }, [location.pathname, handleBeforeUnload])

    // trigger when navigate to another page
    // useEffect(() => {
    //     return () => {
    //         if (confirm('do you want to save the changed content?')) handleSave?.()
    //         else {
    //             localStorage.removeItem(KEYS_OF_DRAFT_DATA)
    //         }
    //     }
    // }, [])


    const toolbarRenderer = useMemo(() => <Toolbar />, [])
    const sidebarRenderer = useMemo(() => <Sidebar />, [])

    const contentRenderer = useMemo(() => (
        <div style={fullscreen ? fullscreenSx : normalscreenSx} >

            {toolbarRenderer}
            {sidebarRenderer}

            {/* main canvas */}
            <ReactFlow
                fitView
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                connectionLineComponent={ConnectionLine}
                defaultEdgeOptions={{ markerEnd: { type: MarkerType.ArrowClosed } }}
                onDrop={onDrop}
                onDragOver={onDragOver}
                deleteKeyCode={'Delete'}
                onCopy={onCopy}
                onPaste={onPaste}
            >
                <Controls showInteractive={false}>
                    <FullSizeBtn onClick={handleFullscreen} />
                </Controls>
                <MiniMap />
                <Background variant="dots" gap={12} size={1} />
            </ReactFlow>

            <Snackbar
                open={snackbar.open}
                message={snackbar.message}
                autoHideDuration={1000}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                transitionDuration={0}
                sx={{
                    '&': {
                        top: '50%',
                        transform: 'translateY(-50%)'
                    },
                    '& .MuiPaper-root': {
                        minWidth: 'auto',
                        backgroundColor: 'rgb(107, 107, 107)'
                    }
                }}
                onClose={() => setSnackbar({ open: false, message: '' })}
            />
        </div>
    ), [initList, nodes, edges, fullscreen, savedSurveyFlow, snackbar])

    return contentRenderer
};


export default SurveyFlow;